import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { logger } from '../services/logger';
import type { UserOrganizationDto } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { userOrganizationsQueryOptions } from '../hooks/queries/useAccountShellQueries';

interface SwitchOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SwitchOrganizationModal({ isOpen, onClose }: SwitchOrganizationModalProps) {
  const { user, switchOrganization } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = user?.tenantId;
  const userId = user?.userId;
  const [organizationResult, setOrganizationResult] = useState<{
    tenantId?: string;
    userId?: string;
    items: UserOrganizationDto[];
  } | null>(null);
  const organizations = organizationResult && organizationResult.tenantId === tenantId && organizationResult.userId === userId
    ? organizationResult.items
    : [];
  const [isLoading, setIsLoading] = useState(false);
  const [switchingTenantId, setSwitchingTenantId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reauthMessage, setReauthMessage] = useState<string | null>(null);

  const handleRequestClose = useCallback(() => {
    if (switchingTenantId) {
      return;
    }

    onClose();
  }, [onClose, switchingTenantId]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleRequestClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleRequestClose]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let active = true;
    const loadOrganizations = async () => {
      setIsLoading(true);
      setError(null);
      setReauthMessage(null);

      try {
        const data = await queryClient.fetchQuery(userOrganizationsQueryOptions(tenantId, userId));
        if (active) {
          setOrganizationResult({ tenantId, userId, items: data });
        }
      } catch (loadError) {
        if (active) {
          logger.error('Failed to load organizations', loadError);
          setError('Failed to load organizations. Please try again.');
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    void loadOrganizations();
    return () => {
      active = false;
    };
  }, [isOpen, queryClient, tenantId, userId]);

  const handleSwitch = async (organization: UserOrganizationDto) => {
    if (organization.isCurrent || switchingTenantId) {
      return;
    }

    setSwitchingTenantId(organization.tenantId);
    setError(null);
    setReauthMessage(null);

    try {
      await switchOrganization(organization.tenantId);
    } catch (switchError) {
      const appError = switchError as { reauthRequired?: boolean; redirectUrl?: string; message?: string };
      const apiError = switchError as { response?: { data?: { reauthRequired?: boolean; redirectUrl?: string; message?: string } }; message?: string };
      const response = appError.reauthRequired
        ? appError
        : apiError.response?.data;

      if (response?.reauthRequired) {
        const message = response.message || 'Re-authentication is required to switch organization.';
        setReauthMessage(message);
        const redirectUrl = response.redirectUrl;
        if (redirectUrl) {
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 1000);
        }
      } else {
        setError(response?.message || apiError.message || 'Failed to switch organization.');
      }
    } finally {
      setSwitchingTenantId(null);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-opacity-80 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="switch-organization-title"
      onClick={handleRequestClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 id="switch-organization-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Switch Organization
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Select the organization you want to work in.
          </p>
        </div>

        <div className="px-6 py-4 space-y-3 overflow-y-auto">
          {isLoading && <p className="text-sm text-gray-600 dark:text-gray-300">Loading organizations...</p>}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
            </div>
          )}

          {reauthMessage && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <p className="text-sm text-amber-800 dark:text-amber-300">{reauthMessage}</p>
            </div>
          )}

          {!isLoading && organizations.length === 0 && !error && (
            <p className="text-sm text-gray-600 dark:text-gray-300">No additional organizations available.</p>
          )}

          {organizations.map((organization) => {
            const initial = organization.tenantName.charAt(0).toUpperCase();
            const isSwitching = switchingTenantId === organization.tenantId;
            return (
              <button
                key={organization.tenantId}
                type="button"
                onClick={() => handleSwitch(organization)}
                disabled={organization.isCurrent || !!switchingTenantId}
                className={`w-full text-left border rounded-lg p-4 transition-colors ${
                  organization.isCurrent
                    ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    {organization.tenantLogo ? (
                      <img
                        src={organization.tenantLogo}
                        alt={`${organization.tenantName} logo`}
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 flex items-center justify-center text-sm font-semibold">
                        {initial}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {organization.tenantName}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Auth: {organization.authMethod.toUpperCase()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {organization.isCurrent && (
                      <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                        Current
                      </span>
                    )}
                    {!organization.isCurrent && isSwitching && (
                      <span className="text-xs text-gray-500 dark:text-gray-300">Switching...</span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            type="button"
            onClick={handleRequestClose}
            disabled={!!switchingTenantId}
            className="btn-secondary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
